import React, { useEffect, useRef, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import {
  AlertCircle,
  Download,
  ExternalLink,
  Eye,
  FileText,
  MessageSquare,
  RefreshCw,
  SendHorizontal,
  Sparkles,
  Upload,
  X,
} from 'lucide-react';
import { scopes } from '../../../../constants/auth';

const INTAKE_API_URL = 'https://ceu-agent-factory-intake.azurewebsites.net/api/factory/intake';
const MAX_FORM_BYTES = 2 * 1024 * 1024;

type ChatRole = 'user' | 'assistant';

/** Vista previa del formulario que devuelve el orquestador (campo `formPreview`). */
interface FormPreview {
  html: string;
  title: string;
  fileName: string;
  url?: string;
  expiresAt?: string;
}

interface ChatMessage {
  id: string;
  role: ChatRole;
  content: string;
  timestamp: string;
  formPreview?: FormPreview;
}

function createSessionId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function formatTimestamp(date = new Date()): string {
  return date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
}

function extractAssistantReply(payload: unknown): string {
  if (typeof payload === 'string' && payload.trim()) {
    return payload.trim();
  }

  if (!payload || typeof payload !== 'object') {
    return 'Respuesta recibida sin contenido legible.';
  }

  const data = payload as Record<string, unknown>;
  if (typeof data.respuesta === 'string' && data.respuesta.trim()) {
    return data.respuesta.trim();
  }

  return 'Respuesta recibida sin contenido legible.';
}

function extractFormPreview(payload: unknown): FormPreview | undefined {
  if (!payload || typeof payload !== 'object') return undefined;
  const raw = (payload as Record<string, unknown>).formPreview;
  if (!raw || typeof raw !== 'object') return undefined;
  const data = raw as Record<string, unknown>;
  if (typeof data.html !== 'string' || !data.html.trim()) return undefined;
  return {
    html: data.html,
    title: typeof data.title === 'string' && data.title.trim() ? data.title.trim() : 'Formulario',
    fileName:
      typeof data.fileName === 'string' && /^[\w.-]+\.html$/.test(data.fileName)
        ? data.fileName
        : 'formulario.html',
    url: typeof data.url === 'string' && /^https:\/\//i.test(data.url) ? data.url : undefined,
    expiresAt: typeof data.expiresAt === 'string' ? data.expiresAt : undefined,
  };
}

function looksLikeHtml(value: string): boolean {
  return /<\/?[a-z][\s\S]*>/i.test(value);
}

function downloadHtml(preview: FormPreview): void {
  const href = URL.createObjectURL(new Blob([preview.html], { type: 'text/html;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = href;
  link.download = preview.fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(href), 1000);
}

/**
 * Tarjeta con la vista previa del formulario. El HTML se pinta en un iframe con
 * `sandbox="allow-scripts"`: sin `allow-same-origin` corre en un origen opaco (no
 * ve la sesión ni el almacenamiento de esta app) y sin `allow-forms` no envía nada.
 */
const FormPreviewCard: React.FC<{ preview: FormPreview }> = ({ preview }) => {
  const [open, setOpen] = useState(false);

  return (
    <div className="lp-intake-chat-preview">
      <div className="lp-intake-chat-preview-head">
        <FileText size={16} aria-hidden="true" />
        <div>
          <strong>Vista previa del formulario</strong>
          <span>{preview.title}</span>
        </div>
      </div>
      <div className="lp-intake-chat-preview-actions">
        <button type="button" onClick={() => setOpen((value) => !value)} aria-expanded={open}>
          <Eye size={14} aria-hidden="true" />
          {open ? 'Ocultar' : 'Ver aquí'}
        </button>
        {preview.url && (
          <a href={preview.url} target="_blank" rel="noopener noreferrer">
            <ExternalLink size={14} aria-hidden="true" />
            Abrir en otra pestaña
          </a>
        )}
        <button type="button" onClick={() => downloadHtml(preview)}>
          <Download size={14} aria-hidden="true" />
          Descargar para editarlo
        </button>
      </div>
      {open && (
        <iframe
          className="lp-intake-chat-preview-frame"
          title={`Vista previa: ${preview.title}`}
          sandbox="allow-scripts"
          referrerPolicy="no-referrer"
          srcDoc={preview.html}
        />
      )}
    </div>
  );
};

interface IntakeChatPanelProps {
  collapsed?: boolean;
  onToggle?: () => void;
}

const IntakeChatPanel: React.FC<IntakeChatPanelProps> = ({ collapsed = false, onToggle }) => {
  const { instance } = useMsal();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [sessionId, setSessionId] = useState<string>(() => createSessionId());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const messagesRef = useRef<HTMLDivElement | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!messagesRef.current) return;
    messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
  }, [messages, busy]);

  const getAccessToken = async (): Promise<string> => {
    const account = instance.getActiveAccount() || instance.getAllAccounts()[0];
    if (!account) {
      throw new Error('No hay una sesión activa de Entra ID.');
    }

    const tokenResponse = await instance.acquireTokenSilent({
      account,
      scopes,
    });

    if (!tokenResponse?.accessToken) {
      throw new Error('No se pudo obtener el token del usuario.');
    }

    return tokenResponse.accessToken;
  };

  /** Envía un turno de la conversación; con `formHtml`, el formulario editado. */
  const enviarTurno = async (texto: string, formHtml?: string) => {
    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: texto,
      timestamp: formatTimestamp(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setBusy(true);
    setError(null);

    try {
      const token = await getAccessToken();
      const response = await fetch(INTAKE_API_URL, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: texto,
          sessionId,
          ...(formHtml ? { formHtml } : {}),
        }),
      });

      const rawText = await response.text();
      let payload: unknown = rawText;
      if (rawText) {
        try {
          payload = JSON.parse(rawText);
        } catch {
          payload = rawText;
        }
      }

      if (!response.ok) {
        const detail =
          typeof payload === 'object' && payload && 'error' in payload
            ? String((payload as { error?: unknown }).error || '')
            : typeof payload === 'string'
              ? payload
              : '';
        throw new Error(detail || `Error ${response.status} al llamar al servicio de intake.`);
      }

      if (payload && typeof payload === 'object' && 'sessionId' in payload) {
        const nextSessionId = String((payload as { sessionId?: unknown }).sessionId || '').trim();
        if (nextSessionId) {
          setSessionId(nextSessionId);
        }
      }

      setMessages((prev) => [
        ...prev,
        {
          id: `assistant-${Date.now()}`,
          role: 'assistant',
          content: extractAssistantReply(payload),
          timestamp: formatTimestamp(),
          formPreview: extractFormPreview(payload),
        },
      ]);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'No se pudo completar la solicitud.';
      setError(message);
      setMessages((prev) => [
        ...prev,
        {
          id: `assistant-error-${Date.now()}`,
          role: 'assistant',
          content: `No he podido responder: ${message}`,
          timestamp: formatTimestamp(),
        },
      ]);
    } finally {
      setBusy(false);
    }
  };

  const handleSubmit = async () => {
    const trimmed = input.trim();
    if (!trimmed || busy) return;
    setInput('');
    await enviarTurno(trimmed);
  };

  /** El usuario devuelve el formulario que ha editado en su editor de texto. */
  const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || busy) return;
    if (file.size > MAX_FORM_BYTES) {
      setError('El formulario editado supera los 2 MB.');
      return;
    }
    const contenido = await file.text();
    await enviarTurno(`He subido el formulario editado (${file.name}).`, contenido);
  };

  const handleNewSession = () => {
    if (busy) return;
    setSessionId(createSessionId());
    setError(null);
    setInput('');
    setMessages([]);
  };

  if (collapsed) {
    return (
      <button
        type="button"
        className="lp-intake-chat-fab"
        onClick={onToggle}
        aria-label="Mostrar chat de intake"
        title="Mostrar chat"
      >
        <MessageSquare size={20} />
      </button>
    );
  }

  return (
    <aside className="lp-intake-chat" aria-label="Chat de intake">
      <div className="lp-intake-chat-header">
        <div>
          <p className="lp-intake-chat-eyebrow">
            <Sparkles size={14} />
            Orquestador de agentes
          </p>
          <h3>ODA</h3>
        </div>
        <div className="lp-intake-chat-header-actions">
          <button
            type="button"
            className="lp-intake-chat-icon-btn"
            onClick={handleNewSession}
            disabled={busy}
            title="Nueva conversación"
            aria-label="Nueva conversación"
          >
            <RefreshCw size={15} />
          </button>
          {onToggle && (
            <button
              type="button"
              className="lp-intake-chat-icon-btn"
              onClick={onToggle}
              title="Ocultar chat"
              aria-label="Ocultar chat"
            >
              <X size={15} />
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="error-alert lp-intake-chat-error">
          <AlertCircle size={16} />
          {error}
        </div>
      )}

      <div className="lp-intake-chat-messages" ref={messagesRef}>
        {messages.map((message) => (
          <article key={message.id} className={`lp-intake-chat-message ${message.role}`}>
            <div className="lp-intake-chat-message-head">
              <span>{message.role === 'user' ? 'Tú' : 'Asistente'}</span>
              <time>{message.timestamp}</time>
            </div>
            {message.role === 'assistant' && looksLikeHtml(message.content) ? (
              <div
                className="lp-intake-chat-message-html"
                dangerouslySetInnerHTML={{ __html: message.content }}
              />
            ) : (
              <p>{message.content}</p>
            )}
            {message.formPreview && <FormPreviewCard preview={message.formPreview} />}
          </article>
        ))}
        {busy && (
          <article className="lp-intake-chat-message assistant is-loading">
            <div className="lp-intake-chat-message-head">
              <span>Asistente</span>
              <time>...</time>
            </div>
            <p>Pensando...</p>
          </article>
        )}
      </div>

      <div className="lp-intake-chat-composer">
        <div className="lp-intake-chat-composer-row">
          <input
            type="text"
            className="lp-intake-chat-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                void handleSubmit();
              }
            }}
            disabled={busy}
            placeholder="Escribe un mensaje..."
            aria-label="Mensaje del chat"
          />
          <input
            ref={fileRef}
            type="file"
            accept=".html,.htm,text/html"
            onChange={(e) => void handleUpload(e)}
            hidden
          />
          <button
            type="button"
            className="lp-intake-chat-upload-btn"
            onClick={() => fileRef.current?.click()}
            disabled={busy}
            title="Subir el formulario editado"
            aria-label="Subir el formulario editado"
          >
            <Upload size={16} />
          </button>
          <button
            type="button"
            className="lp-intake-chat-send-btn"
            onClick={() => void handleSubmit()}
            disabled={busy || !input.trim()}
            aria-label={busy ? 'Enviando' : 'Enviar mensaje'}
          >
            {busy ? <RefreshCw className="spinner-icon" size={16} /> : <SendHorizontal size={16} />}
          </button>
        </div>
      </div>
    </aside>
  );
};

export default IntakeChatPanel;
